import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function OtpDialog({ otp, title, onVerified, onClose }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (!otp) { return; }
    setCode(""); setErr(""); setLeft(otp.expires_in); setResendIn(otp.resend_after);
  }, [otp]);
  useEffect(() => {
    if (!otp) { return; }
    const t = setInterval(() => { setLeft((v) => Math.max(0, v - 1)); setResendIn((v) => Math.max(0, v - 1)); }, 1000);
    return () => clearInterval(t);
  }, [otp]);

  const verify = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/advances/otp/${otp.otp_id}/verify`, { code });
      onVerified(data);
    } catch (e) { setErr(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/advances/otp/${otp.otp_id}/resend`);
      setLeft(data.expires_in); setResendIn(data.resend_after); setCode("");
      toast.success("A new verification code has been sent");
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!otp} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="otp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-800" /> {title}</DialogTitle>
          <DialogDescription>A 6-digit verification code has been sent to the authorised approver. Enter it below to complete the entry. Nothing is saved until the code is verified.</DialogDescription>
        </DialogHeader>
        <Input autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => e.key === "Enter" && code.length === 6 && !busy && verify()}
          className="h-14 text-center text-2xl tracking-[0.5em] money" placeholder="••••••" data-testid="otp-code-input" />
        <div className="flex justify-between text-xs">
          <span className={left === 0 ? "text-red-700 font-semibold" : "text-slate-500"} data-testid="otp-expiry">{left === 0 ? "Code expired, request a new one" : `Code expires in ${mmss(left)}`}</span>
          <button className="text-blue-800 font-semibold disabled:text-slate-400" disabled={busy || resendIn > 0} onClick={resend} data-testid="otp-resend-btn">
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
        </div>
        {err && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="otp-error">{err}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="otp-cancel-btn">Cancel</Button>
          <Button className="bg-blue-800 hover:bg-blue-900" disabled={busy || code.length !== 6 || left === 0} onClick={verify} data-testid="otp-verify-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
